import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { fetchFeedback, updateFeedback /* , deleteFeedback */ } from '../../redux/QuestionBank/FeedbackAction';
import { fetchQuestionsByIds } from '../../redux/QuestionBank/QuestionsAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { draftRawToPlainText } from '../../util/draftToHtml';

const FEEDBACK_TYPE_LABELS = {
  typo: { label: 'Typo', color: 'info', icon: 'fa-spell-check' },
  incorrect_answer: { label: 'Incorrect Answer', color: 'danger', icon: 'fa-times-circle' },
  unclear_question: { label: 'Unclear Question', color: 'warning', icon: 'fa-question-circle' },
  incorrect_explanation: { label: 'Wrong Explanation', color: 'danger', icon: 'fa-book' },
  image_issue: { label: 'Image Issue', color: 'secondary', icon: 'fa-image' },
  other: { label: 'Other', color: 'dark', icon: 'fa-comment' }
};

const STATUS_LABELS = {
  pending: { label: 'Pending', color: 'warning' },
  reviewed: { label: 'Reviewed', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  dismissed: { label: 'Dismissed', color: 'secondary' }
};

const FeedbackList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { feedback, loading } = useSelector(state => state.questionBankFeedback || { feedback: [], loading: false });
  const { subjects } = useSelector(state => state.questionBankSubjects || { subjects: [] });
  const { units } = useSelector(state => state.questionBankUnits || { units: [] });
  const { topics } = useSelector(state => state.questionBankTopics || { topics: [] });

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Load all feedback once on mount
  useEffect(() => {
    dispatch(fetchFeedback());
  }, [dispatch]);

  // Filter client-side based on status
  const filteredFeedback = statusFilter === 'all'
    ? feedback
    : feedback.filter(f => f.status === statusFilter);

  // Load subjects/units/topics for export (if not already loaded)
  useEffect(() => {
    if (!subjects || subjects.length === 0) dispatch(fetchSubjects());
    if (!units || units.length === 0) dispatch(fetchUnits());
    if (!topics || topics.length === 0) dispatch(fetchTopics());
  }, [dispatch, subjects, units, topics]);

  const handleViewDetails = (item) => {
    setSelectedFeedback(item);
    setAdminNotes(item.adminNotes || '');
    setIsDetailModalOpen(true);
  };

  const handleEditQuestion = (questionId) => {
    history.push(`/admin/question-bank/create-question?edit=${questionId}`);
  };

  const handleUpdateStatus = async (feedbackId, newStatus) => {
    setUpdating(true);
    const result = await dispatch(updateFeedback(feedbackId, {
      status: newStatus,
      adminNotes: adminNotes,
      resolvedBy: newStatus === 'resolved' ? 'admin' : null
    }));

    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Feedback marked as ${newStatus}`,
        timer: 2000,
        showConfirmButton: false
      });
      setIsDetailModalOpen(false);
      dispatch(fetchFeedback());
    } else {
      Swal.fire('Error', result.error || 'Failed to update status', 'error');
    }
    setUpdating(false);
  };

  /* const handleDelete = async (feedbackId) => {
    const confirm = await Swal.fire({
      title: 'Delete Feedback?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      confirmButtonText: 'Yes, delete it'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteFeedback(feedbackId));
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          timer: 1500,
          showConfirmButton: false
        });
        setIsDetailModalOpen(false);
      } else {
        Swal.fire('Error', result.error || 'Failed to delete', 'error');
      }
    }
  }; */

  const getPlainText = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    try {
      return draftRawToPlainText(content);
    } catch {
      return '';
    }
  };

  const getCorrectAnswerLabel = (question) => {
    const correctIds = question.correctAnswer || [];
    if (correctIds.length === 0) return '';
    const choices = question.choices || [];
    const idx = choices.findIndex(c => c.choiceId === correctIds[0]);
    if (idx !== -1) return String.fromCharCode(65 + idx); // A, B, C, D
    // Fallback for index-based IDs
    const idToLabel = { '0': 'A', '1': 'B', '2': 'C', '3': 'D' };
    return idToLabel[correctIds[0]] || '';
  };

  // Helpers for subject/unit/topic display names (matching Bulk Edit format)
  const getSubjectDisplay = (subjectId) => {
    const s = (subjects || []).find(s => s.subjectId === subjectId);
    return s ? (s.code ? `${s.code} ${s.name}` : s.name) : '';
  };

  const getUnitDisplay = (unitId) => {
    const u = (units || []).find(u => u.unitId === unitId);
    return u ? (u.code ? `${u.code} ${u.name}` : u.name) : '';
  };

  const getTopicDisplay = (topicId) => {
    if (!topicId) return '';
    const t = (topics || []).find(t => t.topicId === topicId);
    return t ? (t.code ? `${t.code} ${t.name}` : t.name) : '';
  };

  const handleExportFeedback = async () => {
    if (filteredFeedback.length === 0) {
      Swal.fire('No Feedback', 'There is no feedback to export with the current filter.', 'info');
      return;
    }

    setIsExporting(true);

    try {
      // Collect unique question IDs from filtered feedback
      const questionIds = [...new Set(filteredFeedback.map(f => f.questionId).filter(Boolean))];

      // Batch fetch all questions
      const result = await dispatch(fetchQuestionsByIds(questionIds));
      const questionsMap = result.success ? result.questions : {};

      // Build Excel workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'PMR Prep';
      workbook.created = new Date();

      // Sheet name matches what Bulk Edit parser looks for ('questions' or 'export')
      const sheet = workbook.addWorksheet('Questions Export');

      // Columns 0-12: Match Bulk Edit import format exactly
      // Columns 13-15: Feedback context (ignored by parser)
      const headers = [
        'Question Code',                    // Col 0
        'Subject Name',                     // Col 1
        'Unit Name',                        // Col 2
        'Topic Name (Optional)',             // Col 3
        'Question Text',                     // Col 4
        'Choice A',                          // Col 5
        'Choice B',                          // Col 6
        'Choice C',                          // Col 7
        'Choice D',                          // Col 8
        'Correct Answer',                    // Col 9
        'Explanation',                       // Col 10
        'Lesson Reference (Optional)',       // Col 11
        'Tags (comma-separated, optional)',  // Col 12
        'Feedback Type',                     // Col 13 (extra - ignored by parser)
        'Feedback Description',              // Col 14 (extra - ignored by parser)
        'Feedback Reporter',                 // Col 15 (extra - ignored by parser)
        'Feedback Status'                    // Col 16 (extra - ignored by parser)
      ];

      sheet.getRow(1).values = headers;
      sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      sheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };

      sheet.columns = [
        { width: 22 },  // A: Question Code
        { width: 22 },  // B: Subject
        { width: 28 },  // C: Unit
        { width: 22 },  // D: Topic
        { width: 50 },  // E: Question Text
        { width: 30 },  // F: Choice A
        { width: 30 },  // G: Choice B
        { width: 30 },  // H: Choice C
        { width: 30 },  // I: Choice D
        { width: 15 },  // J: Correct Answer
        { width: 50 },  // K: Explanation
        { width: 40 },  // L: Lesson Reference
        { width: 25 },  // M: Tags
        { width: 20 },  // N: Feedback Type
        { width: 50 },  // O: Feedback Description
        { width: 20 },  // P: Feedback Reporter
        { width: 15 }   // Q: Feedback Status
      ];

      filteredFeedback.forEach((item, i) => {
        const question = questionsMap[item.questionId] || {};
        const choices = question.choices || [];
        const typeInfo = FEEDBACK_TYPE_LABELS[item.feedbackType] || FEEDBACK_TYPE_LABELS.other;
        const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.pending;

        const row = sheet.getRow(i + 2);
        row.values = [
          item.questionCode || question.questionCode || '',
          getSubjectDisplay(question.subjectId),
          getUnitDisplay(question.unitId),
          getTopicDisplay(question.topicId),
          getPlainText(question.questionText),
          getPlainText(choices[0]?.text),
          getPlainText(choices[1]?.text),
          getPlainText(choices[2]?.text),
          getPlainText(choices[3]?.text),
          getCorrectAnswerLabel(question),
          getPlainText(question.explanationText),
          getPlainText(question.lessonReference),
          (question.tags || []).join(', '),
          typeInfo.label,
          item.description || '',
          item.userName || item.userHandle || 'Unknown',
          statusInfo.label
        ];

        // Highlight feedback columns with a light yellow background
        row.getCell(14).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
        row.getCell(15).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
        row.getCell(16).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
        row.getCell(17).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
      });

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `feedback_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Exported!',
        html: `<p>${filteredFeedback.length} feedback items exported with full question details.</p><p class="text-muted small mt-2">After making corrections, upload the file in <strong>Bulk Upload &rarr; Bulk Edit</strong> to update questions.</p>`
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire('Export Failed', error.message || 'Failed to export feedback.', 'error');
    }

    setIsExporting(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = feedback.filter(f => f.status === 'pending').length;

  return (
    <>
      <Card className="shadow">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-0">
                <i className="fas fa-comment-alt mr-2 text-warning"></i>
                Question Feedback
                {pendingCount > 0 && statusFilter !== 'pending' && (
                  <Badge color="danger" pill className="ml-2">{pendingCount} pending</Badge>
                )}
              </h3>
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Button
                color="success"
                size="sm"
                className="mr-3"
                onClick={handleExportFeedback}
                disabled={isExporting || loading || filteredFeedback.length === 0}
              >
                {isExporting ? (
                  <Spinner size="sm" className="mr-1" />
                ) : (
                  <i className="fas fa-file-export mr-1"></i>
                )}
                Export{filteredFeedback.length > 0 ? ` (${filteredFeedback.length})` : ''}
              </Button>
              <FormGroup className="mb-0 d-flex align-items-center">
                <Label className="mr-2 mb-0 text-muted small">Status:</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading feedback...</p>
            </div>
          ) : filteredFeedback.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
              <h4>No feedback reports</h4>
              <p className="text-muted">
                {statusFilter === 'pending'
                  ? 'No pending feedback to review.'
                  : 'No feedback found with the selected filter.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-items-center table-hover">
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Question</th>
                    <th scope="col">Type</th>
                    <th scope="col">Reported By</th>
                    <th scope="col">Date</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedback.map((item) => {
                    const typeInfo = FEEDBACK_TYPE_LABELS[item.feedbackType] || FEEDBACK_TYPE_LABELS.other;
                    const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.pending;

                    return (
                      <tr key={item.feedbackId}>
                        <td>
                          <span className="font-weight-bold text-primary">
                            {item.questionCode || item.questionId?.substring(0, 8) || 'N/A'}
                          </span>
                          {item.passageId && (
                            <Badge color="light" className="ml-2 text-muted">Passage</Badge>
                          )}
                        </td>
                        <td>
                          <Badge color={typeInfo.color}>
                            <i className={`fas ${typeInfo.icon} mr-1`}></i>
                            {typeInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <small>{item.userName || item.userHandle || 'Unknown'}</small>
                        </td>
                        <td>
                          <small className="text-muted">{formatDate(item.createdAt)}</small>
                        </td>
                        <td>
                          <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleViewDetails(item)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </Button>
                          {item.questionId && (
                            <Button
                              color="default"
                              size="sm"
                              onClick={() => handleEditQuestion(item.questionId)}
                              title="Edit Question"
                            >
                              <i className="fas fa-pencil-alt"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={isDetailModalOpen} toggle={() => setIsDetailModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsDetailModalOpen(false)}>
          Feedback Details
        </ModalHeader>
        {selectedFeedback && (
          <>
            <ModalBody>
              <Row>
                <Col md="6">
                  <p className="mb-2">
                    <strong>Question:</strong>{' '}
                    <span className="text-primary">{selectedFeedback.questionCode || selectedFeedback.questionId}</span>
                  </p>
                  <p className="mb-2">
                    <strong>Type:</strong>{' '}
                    <Badge color={FEEDBACK_TYPE_LABELS[selectedFeedback.feedbackType]?.color || 'dark'}>
                      {FEEDBACK_TYPE_LABELS[selectedFeedback.feedbackType]?.label || selectedFeedback.feedbackType}
                    </Badge>
                  </p>
                  <p className="mb-2">
                    <strong>Status:</strong>{' '}
                    <Badge color={STATUS_LABELS[selectedFeedback.status]?.color || 'warning'}>
                      {STATUS_LABELS[selectedFeedback.status]?.label || selectedFeedback.status}
                    </Badge>
                  </p>
                </Col>
                <Col md="6">
                  <p className="mb-2">
                    <strong>Reported By:</strong>{' '}
                    {selectedFeedback.userName || selectedFeedback.userHandle}
                  </p>
                  <p className="mb-2">
                    <strong>Date:</strong>{' '}
                    {formatDate(selectedFeedback.createdAt)}
                  </p>
                  {selectedFeedback.testId && (
                    <p className="mb-2">
                      <strong>Test ID:</strong>{' '}
                      <small>{selectedFeedback.testId}</small>
                    </p>
                  )}
                </Col>
              </Row>

              <hr />

              <div className="mb-3">
                <strong>Description:</strong>
                <div className="bg-lighter p-3 rounded mt-2">
                  {selectedFeedback.description}
                </div>
              </div>

              <FormGroup>
                <Label><strong>Admin Notes:</strong></Label>
                <Input
                  type="textarea"
                  rows="3"
                  placeholder="Add notes about how this was resolved..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
              {selectedFeedback.questionId && (
                <Button
                  color="default"
                  size="sm"
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    handleEditQuestion(selectedFeedback.questionId);
                  }}
                >
                  <i className="fas fa-pencil-alt mr-1"></i>
                  Edit Question
                </Button>
              )}
              {/* <Button
                color="danger"
                outline
                size="sm"
                onClick={() => handleDelete(selectedFeedback.feedbackId)}
                disabled={updating}
              >
                <i className="fas fa-trash mr-1"></i>
                Delete
              </Button> */}
              <div>
                {selectedFeedback.status !== 'dismissed' && (
                  <Button
                    color="warning"
                    className="mr-2"
                    onClick={() => handleUpdateStatus(selectedFeedback.feedbackId, 'dismissed')}
                    disabled={updating}
                  >
                    Dismiss
                  </Button>
                )}
                {selectedFeedback.status !== 'reviewed' && (
                  <Button
                    color="info"
                    className="mr-2"
                    onClick={() => handleUpdateStatus(selectedFeedback.feedbackId, 'reviewed')}
                    disabled={updating}
                  >
                    Mark Reviewed
                  </Button>
                )}
                {selectedFeedback.status !== 'resolved' && (
                  <Button
                    color="success"
                    onClick={() => handleUpdateStatus(selectedFeedback.feedbackId, 'resolved')}
                    disabled={updating}
                  >
                    {updating ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="fas fa-check mr-1"></i>
                        Resolve
                      </>
                    )}
                  </Button>
                )}
              </div>
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );
};

export default FeedbackList;
